// ── Static Data for Platform Journey Tool ─────────────────────────────────────
import type {
  AmlScreeningConfig, ApiKeyConfig, ApprovalNamespaceConfig, CascadeCheckConfig, ChecklistItem, ConfidenceLevelBand, DuplicateRuleConfig, EnvId, FraudSignalConfig,
  KybCheckConfig, PortalUserConfig, RiskFactorConfig, RiskLevelBand, SecretConfig, StageId, StageMeta, TmRuleConfig, TmScenario, WebhookConfig,
} from './types'

export const STAGES: StageMeta[] = [
  { id: 'account-setup', label: 'Account Setup', tagline: 'Parent + child shells across environments', live: false },
  { id: 'workflow-builder', label: 'Workflow Builder', tagline: 'Visual workflow editor & node config', live: false },
  { id: 'idv', label: 'IDV & Biometrics', tagline: 'Document capture, face match, liveness', live: true },
  { id: 'kyc', label: 'KYC + Cascade', tagline: 'Identity verification waterfall', live: true },
  { id: 'kyb', label: 'KYB', tagline: 'Business registry, UBO & ownership checks', live: true },
  { id: 'aml', label: 'AML', tagline: 'PEP, sanctions & watchlist screening', live: true },
  { id: 'fraud', label: 'Fraud', tagline: 'Device, IP, email & phone risk', live: true },
  { id: 'tm', label: 'Transaction Monitoring', tagline: 'Velocity, threshold & structuring rules', live: false },
]

export const ENVIRONMENTS: { id: EnvId; label: string }[] = [
  { id: 'dev', label: 'DEV' },
  { id: 'uat', label: 'UAT' },
  { id: 'preprod', label: 'PRE-PROD' },
  { id: 'prod', label: 'PROD' },
]

// Illustrative "what's in scope" checklist per product — a rep can click items
// in/out of scope for this customer before walking through the live/simulated demo.
// Not sourced from a real config API; mirrors the kind of settings Implementations
// actually walks through when standing up a product for a customer.
export const CONFIG_CHECKLISTS: Record<Exclude<StageId, 'account-setup' | 'workflow-builder'>, ChecklistItem[]> = {
  idv: [
    { id: 'doc-capture', label: 'Document capture (OCR)' },
    { id: 'face-match', label: 'Face match & liveness' },
    { id: 'consent', label: 'Consent & review screens' },
    { id: 'hosted-sdk', label: 'Hosted OneSDK flow' },
  ],
  kyc: [
    { id: 'ruleset', label: 'Verification ruleset' },
    { id: 'cascade-sources', label: 'Cascade data sources' },
    { id: 'decisioning', label: 'Workflow decisioning' },
    { id: 'manual-review', label: 'Manual review queue' },
  ],
  aml: [
    { id: 'pep', label: 'PEP screening' },
    { id: 'sanctions', label: 'Sanctions screening' },
    { id: 'adverse-media', label: 'Adverse media' },
    { id: 'ongoing-monitoring', label: 'Ongoing monitoring' },
  ],
  fraud: [
    { id: 'device', label: 'Device fingerprinting' },
    { id: 'ip-risk', label: 'IP risk scoring' },
    { id: 'contact-risk', label: 'Email & phone risk' },
  ],
  tm: [
    { id: 'structuring', label: 'Structuring rule' },
    { id: 'velocity', label: 'Velocity rule' },
    { id: 'threshold', label: 'Threshold rule' },
    { id: 'alert-routing', label: 'Alert routing' },
  ],
  kyb: [
    { id: 'registry-lookup', label: 'Business registry lookup (ABN/ASIC)' },
    { id: 'ownership-ubo', label: 'Ownership & UBO identification' },
    { id: 'entity-screening', label: 'AML entity screening' },
    { id: 'trust-analyser', label: 'Trust structure analysis' },
  ],
}

export const TM_SCENARIOS: TmScenario[] = [
  {
    id: 'clean',
    label: 'Clean activity',
    description: 'A handful of everyday transactions — nothing trips a rule.',
    transactions: [
      { id: 'tx_1', timestamp: '2026-07-24T09:12:00Z', amount: 84.5, currency: 'AUD', direction: 'OUT', channel: 'Card', counterparty: 'Woolworths' },
      { id: 'tx_2', timestamp: '2026-07-26T18:41:00Z', amount: 220, currency: 'AUD', direction: 'OUT', channel: 'PayID', counterparty: 'J. Nguyen' },
      { id: 'tx_3', timestamp: '2026-07-29T11:05:00Z', amount: 3400, currency: 'AUD', direction: 'IN', channel: 'Bank Transfer', counterparty: 'Payroll Pty Ltd' },
      { id: 'tx_4', timestamp: '2026-07-30T20:02:00Z', amount: 65, currency: 'AUD', direction: 'OUT', channel: 'Card', counterparty: 'Netflix' },
    ],
  },
  {
    id: 'structuring',
    label: 'Structuring pattern',
    description: 'Multiple transfers just under the AUD 10,000 reporting threshold in a short window.',
    transactions: [
      { id: 'tx_1', timestamp: '2026-07-29T08:03:00Z', amount: 9850, currency: 'AUD', direction: 'OUT', channel: 'Bank Transfer', counterparty: 'Silverline Holdings' },
      { id: 'tx_2', timestamp: '2026-07-29T14:47:00Z', amount: 9700, currency: 'AUD', direction: 'OUT', channel: 'Bank Transfer', counterparty: 'Silverline Holdings' },
      { id: 'tx_3', timestamp: '2026-07-30T09:20:00Z', amount: 9600, currency: 'AUD', direction: 'OUT', channel: 'Bank Transfer', counterparty: 'Silverline Holdings' },
      { id: 'tx_4', timestamp: '2026-07-30T21:55:00Z', amount: 120, currency: 'AUD', direction: 'OUT', channel: 'Card', counterparty: 'BP Fuel' },
    ],
  },
  {
    id: 'velocity',
    label: 'High-risk velocity',
    description: 'A burst of high-value outbound transfers to a new counterparty within hours.',
    transactions: [
      { id: 'tx_1', timestamp: '2026-07-30T22:01:00Z', amount: 18000, currency: 'AUD', direction: 'OUT', channel: 'Bank Transfer', counterparty: 'New Payee — Offshore Ltd' },
      { id: 'tx_2', timestamp: '2026-07-30T22:34:00Z', amount: 22000, currency: 'AUD', direction: 'OUT', channel: 'Bank Transfer', counterparty: 'New Payee — Offshore Ltd' },
      { id: 'tx_3', timestamp: '2026-07-30T23:10:00Z', amount: 15500, currency: 'AUD', direction: 'OUT', channel: 'Bank Transfer', counterparty: 'New Payee — Offshore Ltd' },
      { id: 'tx_4', timestamp: '2026-07-31T00:02:00Z', amount: 9800, currency: 'AUD', direction: 'OUT', channel: 'Crypto Exchange', counterparty: 'Unhosted Wallet' },
    ],
  },
]

// ── KYC + Cascade — reimagined "which checks run, in what order" configuration ─
// Deliberately NOT the drag-and-drop Cascade/Workflow Builder — a simplified,
// plain-English on/off + reorder list standing in for that server-side design.
export const DEFAULT_CASCADE_CHECKS: CascadeCheckConfig[] = [
  { id: 'document', label: 'Document Verification', description: 'Checks the physical document (passport, licence, ID card) is genuine and unaltered.', enabled: true },
  { id: 'database', label: 'Government & Credit Bureau Database', description: 'Cross-checks the applicant against DVS, Equifax, Centrix or similar national data sources.', enabled: true },
  { id: 'biometric', label: 'Biometric / Selfie Match', description: 'Matches a live selfie against the document photo to confirm the applicant is who the document says.', enabled: true },
  { id: 'pep-sanctions', label: 'PEP & Sanctions Screening', description: 'Screens the applicant against politically-exposed-persons and sanctions watchlists.', enabled: true },
  { id: 'manual-review', label: 'Manual Review Fallback', description: 'Routes anything inconclusive to a human reviewer instead of auto-failing.', enabled: true },
]

// ── AML — screening configuration ─────────────────────────────────────────────
export const DEFAULT_AML_CONFIG: AmlScreeningConfig = {
  sanctions: true,
  pep: true,
  adverseMedia: true,
  ongoingMonitoring: false,
}

// ── Fraud — signal configuration, mirrors DealHub Scope's `rules_fraud_types` ──
export const DEFAULT_FRAUD_SIGNALS: FraudSignalConfig[] = [
  { id: 'device_intelligence', label: 'Device Intelligence', description: 'Fingerprints the device used to apply — flags emulators, jailbreaks and known-bad devices.', enabled: true },
  { id: 'behavioural_biometrics', label: 'Behavioural Biometrics', description: 'Looks at typing/interaction patterns for signs of automation or coaching.', enabled: false },
  { id: 'phone_risk', label: 'Phone Risk Assessment', description: 'Flags recently-ported, VOIP or otherwise high-risk phone numbers.', enabled: true },
  { id: 'email_risk', label: 'Email Risk Assessment', description: 'Flags disposable, newly-created or otherwise high-risk email addresses.', enabled: true },
]

// ── Duplicate Detection — default rules mirror Operations Center's Duplicates tab ─
// (7 platform-default rules, always active; customers can layer custom rules on top).
export const DEFAULT_DUPLICATE_RULES: DuplicateRuleConfig[] = [
  { id: 'external-reference', name: 'External reference', matchFields: ['EXTERNAL_REFERENCE'], method: 'EXACT', strength: 'VERY_HIGH', custom: false },
  { id: 'document-identifiers', name: 'Document identifiers', matchFields: ['DOC_PRIMARY_IDENTIFIER', 'DOC_SECONDARY_IDENTIFIER', 'DOC_COUNTRY', 'DOC_SUBDIVISION', 'DOC_TYPE'], method: 'EXACT', strength: 'VERY_HIGH', custom: false },
  { id: 'phone-number', name: 'Phone number', matchFields: ['PHONE_NUMBER'], method: 'EXACT', strength: 'HIGH', custom: false },
  { id: 'email-address', name: 'Email address', matchFields: ['EMAIL_ADDRESS'], method: 'EXACT', strength: 'MEDIUM', custom: false },
  { id: 'given-family-name', name: 'Given + Family name', matchFields: ['GIVEN_NAME', 'FAMILY_NAME'], method: 'EXACT', strength: 'MEDIUM', custom: false },
  { id: 'given-family-dob', name: 'Given + Family name + Date of birth', matchFields: ['GIVEN_NAME', 'FAMILY_NAME', 'DATE_OF_BIRTH'], method: 'EXACT', strength: 'VERY_HIGH', custom: false },
  { id: 'given-family-address', name: 'Given + Family name + Short form normalised address', matchFields: ['ADDR_NORM_SHORT', 'GIVEN_NAME', 'FAMILY_NAME'], method: 'EXACT', strength: 'HIGH', custom: false },
]

// ── Transaction Monitoring — rule thresholds that genuinely drive the simulation ─
export const DEFAULT_TM_RULE_CONFIG: TmRuleConfig = {
  reportingThreshold: 10000,
  structuringWindowHours: 48,
  structuringMinCount: 3,
  velocityWindowHours: 24,
  velocityThreshold: 20000,
  flagNewPayee: true,
}

// ── KYB — Australian business checks (mirrors Scope's kybConfig.auChecks) ────
export const DEFAULT_KYB_AU_CHECKS: KybCheckConfig[] = [
  { id: 'aml-standard', label: 'AML Standard (PEPs, Sanctions)', description: 'Screens the business and its associated individuals against PEP databases and sanctions lists.', enabled: true },
  { id: 'aml-enhanced', label: 'AML Enhanced (+ Adverse Media)', description: 'Standard AML checks plus adverse media screening for negative news coverage.', enabled: false },
  { id: 'asic-info', label: 'ASIC Information (Current)', description: 'Retrieves current company information from the ASIC registry — structure, directors, shareholders, status.', enabled: true },
  { id: 'ubo', label: 'UBO (Ultimate Beneficial Ownership)', description: 'Identifies the natural persons who ultimately own or control the business, tracing through corporate layers and trusts.', enabled: true },
  { id: 'credit-report', label: 'Business Credit Report (CreditorWatch)', description: 'Business credit report covering creditworthiness, payment history, court judgments and defaults.', enabled: false },
]

// ── KYB — Ownership Insights (mirrors Operations Center's Ownership tab) ─────
export const DEFAULT_KYB_OWNERSHIP: { uboThresholdPercent: number; blockingThresholdPercent: number } = {
  uboThresholdPercent: 25,
  blockingThresholdPercent: 25,
}

export const DEFAULT_KYB_BLOCKING_DEFINITIONS: KybCheckConfig[] = [
  { id: 'org-not-found', label: 'Organisation Not Found', description: 'Provider could not find the organisation. All shareholder edges pruned.', enabled: true },
  { id: 'no-shareholders', label: 'No Shareholders Found', description: 'No shareholder data from provider. All shareholder edges pruned.', enabled: true },
  { id: 'ownership-exceeds-100', label: 'Ownership Exceeds 100%', description: 'Reported ownership percentages for an entity sum to more than 100%.', enabled: true },
  { id: 'circular-ownership', label: 'Circular Ownership Detected', description: 'An ownership loop was detected between two or more entities.', enabled: true },
  { id: 'shareholder-data-incomplete', label: 'Shareholder Data Incomplete', description: 'Shareholder record is missing fields needed to resolve UBOs.', enabled: true },
  { id: 'trust-structure-unresolved', label: 'Trust Structure Unresolved', description: 'A trust in the ownership chain could not be fully resolved to individuals.', enabled: true },
  { id: 'provider-timeout', label: 'Provider Timeout', description: 'The registry provider did not respond within the expected time.', enabled: true },
  { id: 'manual-review-required', label: 'Manual Review Required', description: 'Ownership structure is too complex to auto-resolve — routed to a human reviewer.', enabled: true },
]

export const DEFAULT_KYB_TRUST_SPLIT = {
  enabled: false,
  unitPercent: 20,
  discretionaryPercent: 60,
  smsfPercent: 15,
  otherPercent: 5,
}

// ── Risk Profile — mirrors Operations Center's Risk Profiles configuration ───
export const DEFAULT_RISK_LEVELS: RiskLevelBand[] = [
  { id: 'low', label: 'LOW', color: '#15803d', minScore: 0, maxScore: 30 },
  { id: 'medium', label: 'MEDIUM', color: '#c2410c', minScore: 31, maxScore: 50 },
  { id: 'high', label: 'HIGH', color: '#b91c1c', minScore: 51, maxScore: 75 },
  { id: 'unacceptable', label: 'UNACCEPTABLE', color: '#7f1d1d', minScore: 76, maxScore: null },
]

export const DEFAULT_CONFIDENCE_LEVELS: ConfidenceLevelBand[] = [
  { id: 'very-low', label: 'VERY_LOW', adjustment: 15, minScore: null, maxScore: 25 },
  { id: 'low', label: 'LOW', adjustment: 10, minScore: 26, maxScore: 50 },
  { id: 'medium', label: 'MEDIUM', adjustment: 5, minScore: 51, maxScore: 74 },
  { id: 'high', label: 'HIGH', adjustment: 1, minScore: 75, maxScore: null },
]

export const DEFAULT_RISK_FACTORS: RiskFactorConfig[] = [
  { id: 'high-risk-country', label: 'High-risk country of residence', category: 'General', weight: 20, enabled: true },
  { id: 'pep-match', label: 'PEP match', category: 'General', weight: 25, enabled: true },
  { id: 'sanctions-match', label: 'Sanctions match', category: 'General', weight: 30, enabled: true },
  { id: 'adverse-media', label: 'Adverse media hit', category: 'General', weight: 15, enabled: true },
  { id: 'cash-intensive-occupation', label: 'Cash-intensive occupation', category: 'General', weight: 10, enabled: true },
  { id: 'complex-ownership', label: 'Complex ownership structure', category: 'General', weight: 15, enabled: false },
  { id: 'high-risk-industry', label: 'High-risk industry', category: 'General', weight: 10, enabled: true },
  { id: 'pep-associate', label: 'Politically exposed associate', category: 'General', weight: 20, enabled: false },
]

// ── Platform — Portal users (mirrors Operations Center's Portal tab) ─────────
export const DEFAULT_PORTAL_USERS: PortalUserConfig[] = [
  { id: 'user-1', name: 'Sam Karakai', email: 'sam.karakai@example.com', active: true, locked: false, accountType: 'Child User', roles: ['App-Role-ITOps', 'App-Role-CustomerService', 'App-Role-Admin'], failedAttempts: 0, createdDate: '2026-02-02' },
  { id: 'user-2', name: 'Tom Richards', email: 'tom.richards@example.com', active: true, locked: false, accountType: 'Child User', roles: ['App-Role-ITOps', 'App-Role-CustomerService', 'App-Role-Admin'], failedAttempts: 0, createdDate: '2026-02-02' },
  { id: 'user-3', name: 'Support Account', email: 'support@example.com', active: true, locked: false, accountType: 'Child User', roles: ['App-Role-ITOps', 'App-Role-CustomerService'], failedAttempts: 0, createdDate: '2026-02-05' },
]

// ── Platform — Webhooks (mirrors Operations Center's Webhooks tab) ───────────
export const DEFAULT_WEBHOOK_CONFIG: WebhookConfig = {
  notificationsEnabled: true,
  jwtSecurityEnabled: true,
  endpoints: [
    { id: 'endpoint-1', url: 'https://api.your-company.com/api/compliance/v1/webhooks/frankieone' },
  ],
}

// ── Platform — API Access & Secrets ───────────────────────────────────────────
export const DEFAULT_API_KEYS: ApiKeyConfig[] = [
  { id: 'key-1', label: 'Legacy Key', tag: 'Legacy', createdDate: '2025-10-23', activatedDate: '—', expiresDate: '—' },
]

export const DEFAULT_SECRETS: SecretConfig[] = []

// ── Platform — Approvals/Review (namespace/version approval workflow) ────────
export const DEFAULT_APPROVAL_NAMESPACES: ApprovalNamespaceConfig[] = [
  { id: 'ns-complyadvantage', namespace: 'con_complyadvantage', activeVersion: 'from git', latestApproved: '—', status: 'APPROVED', sourceType: 'git', reviewable: false },
  { id: 'ns-daon', namespace: 'con_daon', activeVersion: 'v15', latestApproved: 'v15', status: 'APPROVED', sourceType: 'version', reviewable: true },
  { id: 'ns-incode', namespace: 'con_incode', activeVersion: 'v3', latestApproved: 'v3', status: 'APPROVED', sourceType: 'version', reviewable: true },
  { id: 'ns-customer', namespace: 'customer', activeVersion: 'from git', latestApproved: '—', status: 'APPROVED', sourceType: 'git', reviewable: false },
  { id: 'ns-doc-processor', namespace: 'doc_processor', activeVersion: 'from git', latestApproved: '—', status: 'APPROVED', sourceType: 'git', reviewable: false },
  { id: 'ns-entity-profiles', namespace: 'entity_profiles', activeVersion: 'v6', latestApproved: 'v6', status: 'APPROVED', sourceType: 'version', reviewable: true },
  { id: 'ns-features', namespace: 'features', activeVersion: 'from git', latestApproved: '—', status: 'APPROVED', sourceType: 'git', reviewable: false },
  { id: 'ns-idv-service', namespace: 'idv_service', activeVersion: 'v9', latestApproved: 'v9', status: 'APPROVED', sourceType: 'version', reviewable: true },
  { id: 'ns-kycutility', namespace: 'kycutility', activeVersion: 'from git', latestApproved: '—', status: 'APPROVED', sourceType: 'git', reviewable: false },
  { id: 'ns-onesdk-profiles', namespace: 'onesdk_profiles', activeVersion: 'from git', latestApproved: '—', status: 'APPROVED', sourceType: 'git', reviewable: false },
  { id: 'ns-onesdk-utility', namespace: 'onesdk_utility', activeVersion: 'v7', latestApproved: 'v7', status: 'APPROVED', sourceType: 'version', reviewable: true },
]
